import { useState, useEffect } from 'react';
import { GA_COUNTIES } from '../lib/gaCounties';
import { supabase } from '../lib/supabase';
import { sanitise } from '../lib/audit';
import { listPublicPhysicians } from '../lib/directory';
import PhysicianPicker from '../components/directory/PhysicianPicker';
import { CaeraLogo } from '../components/CaeraLogo';
import PetsQuestion from '../components/PetsQuestion';
import { PET_COPY, petAnswerError, petColumns } from '../lib/pets';
import { APP_HOST, SUPPORT_EMAIL } from '../lib/org';

// Dropdown options that get stored directly to the `leads` table keep a
// stable English `value` regardless of form language — only the displayed
// `label` is translated. (IntakeForm.js's referral-source list predates this
// convention and stores the localized text directly; not fixed here since
// nothing currently filters on it, but not worth repeating going forward.)
const INSURANCE_OPTIONS = [
  { value: 'Medicaid — Amerigroup', label: { en: 'Medicaid — Amerigroup', es: 'Medicaid — Amerigroup' } },
  { value: 'Medicaid — Peach State', label: { en: 'Medicaid — Peach State', es: 'Medicaid — Peach State' } },
  { value: 'Medicaid — WellCare', label: { en: 'Medicaid — WellCare', es: 'Medicaid — WellCare' } },
  { value: 'Medicaid — CareSource', label: { en: 'Medicaid — CareSource', es: 'Medicaid — CareSource' } },
  { value: 'Medicaid — Magnolia Health', label: { en: 'Medicaid — Magnolia Health', es: 'Medicaid — Magnolia Health' } },
  { value: 'Private Insurance', label: { en: 'Private Insurance', es: 'Seguro Privado' } },
  { value: 'Self-Pay / Private Pay', label: { en: 'Self-Pay / Private Pay', es: 'Pago Propio / Pago Privado' } },
  { value: 'Other', label: { en: 'Other', es: 'Otro' } },
];

const REFERRING_TYPES = [
  { key: 'physician', label: { en: '🩺 Physician / Hospital', es: '🩺 Médico / Hospital' } },
  { key: 'agency',    label: { en: '🏢 Agency / Organisation', es: '🏢 Agencia / Organización' } },
  { key: 'self',      label: { en: '👨‍👩‍👧 Self / Family', es: '👨‍👩‍👧 Yo mismo / Familia' } },
];

const CONTACT_TIMES = [
  { value: 'Anytime', label: { en: 'Anytime', es: 'Cualquier momento' } },
  { value: 'Morning (8am – 12pm)', label: { en: 'Morning (8am – 12pm)', es: 'Mañana (8am – 12pm)' } },
  { value: 'Afternoon (12pm – 5pm)', label: { en: 'Afternoon (12pm – 5pm)', es: 'Tarde (12pm – 5pm)' } },
  { value: 'Evening (5pm – 7pm)', label: { en: 'Evening (5pm – 7pm)', es: 'Noche (5pm – 7pm)' } },
  { value: 'Email preferred', label: { en: 'Email preferred', es: 'Prefiero correo electrónico' } },
];

const RELATIONSHIPS = [
  { value: 'Referring Physician', label: { en: 'Referring Physician', es: 'Médico Remitente' } },
  { value: 'Nurse / NP', label: { en: 'Nurse / NP', es: 'Enfermero(a) / NP' } },
  { value: 'Social Worker / Case Manager', label: { en: 'Social Worker / Case Manager', es: 'Trabajador(a) Social / Gestor(a) de Casos' } },
  { value: 'Hospital Discharge Planner', label: { en: 'Hospital Discharge Planner', es: 'Planificador(a) de Altas Hospitalarias' } },
  { value: 'Agency Staff', label: { en: 'Agency Staff', es: 'Personal de la Agencia' } },
  { value: 'Parent / Guardian', label: { en: 'Parent / Guardian', es: 'Padre/Madre / Tutor(a)' } },
  { value: 'Family Member', label: { en: 'Family Member', es: 'Familiar' } },
  { value: 'Other', label: { en: 'Other', es: 'Otro' } },
];

const TRANSLATIONS = {
  en: {
    topbarTitle: 'Patient Referral — A Heart For Care',
    topbarSub: `🔒 Secure referral submission · ${APP_HOST}/referral`,
    heading: 'Patient Referral Form',
    subDesc: 'Submit a referral for a patient requiring home nursing services. All submissions go directly to our care coordination team.',
    fieldsMarkedPrefix: 'Fields marked', fieldsMarkedSuffix: 'are required.',
    steps: ['Service Type', 'Patient Info', 'Referring Provider', 'Submitter & Consent'],
    // Step 1
    selectServiceType: 'Payer', gappTitle: 'GAPP',
    gappDesc: 'Georgia Pediatric Program — Medicaid-funded nursing & personal care for medically fragile children under 21',
    medicaidTitle: 'Medicaid', medicaidDesc: 'Regular Medicaid, not through GAPP — typically supplies only',
    insuranceTitle: 'Private Insurance / CMO', insuranceDesc: 'A commercial plan, or a Medicaid CMO (Amerigroup, CareSource, Peach State)',
    selfPayTitle: 'Self-Pay', selfPayDesc: 'The family will pay for services directly',
    selectServices: 'What is being referred for?', servicesError: 'Select at least one service.',
    lineHomeHealth: 'Home Health Nursing', lineHomeHealthDesc: 'Skilled nursing and/or personal support in the home',
    lineDme: 'Equipment & Supplies (DME)', lineDmeDesc: 'Incontinence, ostomy, catheter and other supplies',
    serviceTypeError: 'Please select a service type.', continuePatientInfo: 'Continue → Patient Info',
    // Step 2
    patientInformation: 'Patient Information', firstName: 'First Name', lastName: 'Last Name', dob: 'Date of Birth',
    phoneNumber: 'Phone Number', homeAddress: 'Home Address', county: 'County', selectCounty: '— Select County —',
    primaryDiagnosis: 'Primary Diagnosis', primaryDiagnosisPlaceholder: 'e.g. Cerebral Palsy',
    secondaryDiagnosis: 'Secondary Diagnosis', secondaryDiagnosisPlaceholder: 'If applicable',
    careNotes: 'Care Needs / Special Notes', careNotesPlaceholder: 'Describe specific care requirements, mobility needs, behavioural considerations…',
    insuranceFunding: 'Insurance & Funding', insuranceProvider: 'Insurance Provider', selectPlaceholder: '— Select —',
    medicaidId: 'Medicaid ID / Member ID', medicaidIdPlaceholder: 'Member ID number',
    insurancePlan: 'Insurance Plan / Group No.', insurancePlanPlaceholder: 'Plan or group number',
    authNumber: 'Auth Number (if obtained)', authNumberPlaceholder: 'Pre-auth number',
    continueReferringProvider: 'Continue → Referring Provider', back: '← Back', required: 'Required',
    // Step 3
    referralType: 'Referral Type', providerDetails: 'Provider Details',
    physicianAgencyName: 'Physician / Agency Name', physicianAgencyPlaceholder: 'Dr. Jane Doe / KidMed GA',
    findPhysician: 'Find the physician in our list', findPhysicianPlaceholder: 'Physician name or practice…', physicianNotListed: (q) => <>Not listed? <b>Enter their details below</b></>, physicianNoMatch: (q) => `No physician named “${q}” in our list yet — enter their details below.`,
    npiNumber: 'NPI Number', npiPlaceholder: '10-digit NPI', practiceOrg: 'Practice / Organisation',
    practiceOrgPlaceholder: 'Practice or hospital name', phone: 'Phone', phonePlaceholder: '(470) 000-0000',
    fax: 'Fax', email: 'Email', emailPlaceholder: 'doctor@practice.com',
    preferredContactTime: 'Preferred Contact Time', continueSubmitterInfo: 'Continue → Submitter Info',
    // Step 4
    personSubmitting: 'Person Submitting This Form', maybeDifferent: 'May be different from referring physician',
    sameAsProviderLabel: 'I am the referring provider — my details are the same as above',
    relationshipToPatient: 'Relationship to Patient',
    consentText: 'I confirm that I have obtained appropriate consent from the patient or their legal guardian to submit this referral, and that the information provided is accurate to the best of my knowledge.',
    consentError: 'Please confirm consent to proceed.',
    submitErrorMsg: 'Something went wrong. Please try again or call us at (470) 470-6640.',
    submitting: 'Submitting…', submitReferral: '✓ Submit Referral',
    // Success
    referralSubmitted: 'Referral Submitted',
    successMsgPrefix: 'Your referral for', successMsgSuffix: "has been received and sent securely to our care coordination team. We'll be in touch within 1 business day.",
    referenceNumber: 'Reference number', questionsCall: 'Questions? Call', orEmail: 'or email',
  },
  es: {
    topbarTitle: 'Referencia de Paciente — A Heart For Care',
    topbarSub: `🔒 Envío seguro de referencia · ${APP_HOST}/referral`,
    heading: 'Formulario de Referencia de Paciente',
    subDesc: 'Envíe una referencia para un paciente que necesita servicios de enfermería en el hogar. Todos los envíos van directamente a nuestro equipo de coordinación de cuidados.',
    fieldsMarkedPrefix: 'Los campos marcados con', fieldsMarkedSuffix: 'son obligatorios.',
    steps: ['Tipo de Servicio', 'Información del Paciente', 'Médico Remitente', 'Remitente y Consentimiento'],
    // Step 1
    selectServiceType: 'Pagador', gappTitle: 'GAPP',
    gappDesc: 'Programa Pediátrico de Georgia — enfermería y cuidado personal financiado por Medicaid para niños médicamente frágiles menores de 21 años',
    medicaidTitle: 'Medicaid', medicaidDesc: 'Medicaid regular, no a través de GAPP — generalmente solo suministros',
    insuranceTitle: 'Seguro Privado / CMO', insuranceDesc: 'Un plan comercial, o un CMO de Medicaid (Amerigroup, CareSource, Peach State)',
    selfPayTitle: 'Pago Directo', selfPayDesc: 'La familia pagará los servicios directamente',
    selectServices: '¿Para qué se hace la referencia?', servicesError: 'Seleccione al menos un servicio.',
    lineHomeHealth: 'Enfermería en el Hogar', lineHomeHealthDesc: 'Enfermería especializada y/o apoyo personal en el hogar',
    lineDme: 'Equipo y Suministros (DME)', lineDmeDesc: 'Suministros de incontinencia, ostomía, catéteres y otros',
    serviceTypeError: 'Por favor seleccione un tipo de servicio.', continuePatientInfo: 'Continuar → Información del Paciente',
    // Step 2
    patientInformation: 'Información del Paciente', firstName: 'Nombre', lastName: 'Apellido', dob: 'Fecha de Nacimiento',
    phoneNumber: 'Número de Teléfono', homeAddress: 'Dirección del Hogar', county: 'Condado', selectCounty: '— Seleccionar Condado —',
    primaryDiagnosis: 'Diagnóstico Primario', primaryDiagnosisPlaceholder: 'ej. Parálisis Cerebral',
    secondaryDiagnosis: 'Diagnóstico Secundario', secondaryDiagnosisPlaceholder: 'Si corresponde',
    careNotes: 'Necesidades de Cuidado / Notas Especiales', careNotesPlaceholder: 'Describa los requisitos de cuidado específicos, necesidades de movilidad, consideraciones de comportamiento…',
    insuranceFunding: 'Seguro y Financiamiento', insuranceProvider: 'Proveedor de Seguro', selectPlaceholder: '— Seleccionar —',
    medicaidId: 'Número de Medicaid / ID de Miembro', medicaidIdPlaceholder: 'Número de ID de miembro',
    insurancePlan: 'Plan de Seguro / No. de Grupo', insurancePlanPlaceholder: 'Número de plan o grupo',
    authNumber: 'Número de Autorización (si se obtuvo)', authNumberPlaceholder: 'Número de preautorización',
    continueReferringProvider: 'Continuar → Médico Remitente', back: '← Atrás', required: 'Requerido',
    // Step 3
    referralType: 'Tipo de Referencia', providerDetails: 'Detalles del Proveedor',
    physicianAgencyName: 'Nombre del Médico / Agencia', physicianAgencyPlaceholder: 'Dra. Jane Doe / KidMed GA',
    findPhysician: 'Busque al médico en nuestra lista', findPhysicianPlaceholder: 'Nombre del médico o consultorio…', physicianNotListed: (q) => <>¿No aparece? <b>Escriba sus datos abajo</b></>, physicianNoMatch: (q) => `Todavía no tenemos un médico llamado “${q}” — escriba sus datos abajo.`,
    npiNumber: 'Número NPI', npiPlaceholder: 'NPI de 10 dígitos', practiceOrg: 'Consultorio / Organización',
    practiceOrgPlaceholder: 'Nombre del consultorio u hospital', phone: 'Teléfono', phonePlaceholder: '(470) 000-0000',
    fax: 'Fax', email: 'Correo Electrónico', emailPlaceholder: 'doctor@practice.com',
    preferredContactTime: 'Horario de Contacto Preferido', continueSubmitterInfo: 'Continuar → Información del Remitente',
    // Step 4
    personSubmitting: 'Persona que Envía este Formulario', maybeDifferent: 'Puede ser diferente del médico remitente',
    sameAsProviderLabel: 'Soy el médico remitente — mis datos son los mismos que arriba',
    relationshipToPatient: 'Relación con el Paciente',
    consentText: 'Confirmo que he obtenido el consentimiento apropiado del paciente o su tutor legal para enviar esta referencia, y que la información proporcionada es precisa a mi leal saber y entender.',
    consentError: 'Por favor confirme el consentimiento para continuar.',
    submitErrorMsg: 'Algo salió mal. Por favor intente de nuevo o llámenos al (470) 470-6640.',
    submitting: 'Enviando…', submitReferral: '✓ Enviar Referencia',
    // Success
    referralSubmitted: 'Referencia Enviada',
    successMsgPrefix: 'Su referencia para', successMsgSuffix: 'ha sido recibida y enviada de forma segura a nuestro equipo de coordinación de cuidados. Nos pondremos en contacto dentro de 1 día hábil.',
    referenceNumber: 'Número de referencia', questionsCall: '¿Preguntas? Llame al', orEmail: 'o envíe un correo a',
  },
};

const EMPTY = {
  // Step 1
  service_type: '',
  service_lines: [],
  // Step 2
  first_name: '', last_name: '', date_of_birth: '', phone: '',
  patient_address: '', county: '',
  primary_diagnosis: '', secondary_diagnosis: '', care_notes: '',
  insurance_provider: '', medicaid_id: '', insurance_plan: '', auth_number: '',
  // Step 3
  referring_type: 'physician',
  referring_physician_id: null,
  referring_provider_name: '', referring_practice: '', referring_npi: '',
  referring_phone: '', referring_fax: '', referring_email: '', referring_contact_time: 'Anytime',
  // Step 4
  same_as_provider: false,
  referrer_first_name: '', referrer_last_name: '',
  referrer_phone: '', referrer_email: '', referrer_relationship: 'Referring Physician',
  consent: false,
};

function generateRefId() {
  return 'REF-' + new Date().getFullYear() + '-' + String(Math.floor(Math.random() * 9000) + 1000);
}

export default function Referral() {
  const [lang, setLang] = useState('en');
  const t = TRANSLATIONS[lang];
  // Asked only when the family refers themselves; a referring doctor or
  // agency is not expected to know the household.
  const [pets, setPets] = useState({ pets: [], other: '', notes: '' });
  const [step, setStep]         = useState(1);
  const [form, setForm]         = useState(EMPTY);
  const [errors, setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [refId]                     = useState(generateRefId);
  const [submitError, setSubmitError] = useState('');

  const set = (field, value) => {
    setForm(p => ({ ...p, [field]: value }));
    if (errors[field]) setErrors(p => { const n = { ...p }; delete n[field]; return n; });
  };

  const toggleServiceLine = (line) => {
    setForm(f => {
      const service_lines = f.service_lines.includes(line)
        ? f.service_lines.filter(l => l !== line)
        : [...f.service_lines, line];
      // GAPP stops being offered without nursing — don't leave it selected.
      const dropGapp = f.service_type === 'gapp' && !service_lines.includes('home_health');
      return { ...f, service_lines, ...(dropGapp ? { service_type: '' } : {}) };
    });
  };

  // ── Validation per step ──────────────────────────────────────
  const validate = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.service_lines.length) e.service_lines = t.servicesError;
      if (!form.service_type) e.service_type = t.serviceTypeError;
    }
    if (s === 2) {
      if (!form.first_name.trim())    e.first_name    = t.required;
      if (!form.last_name.trim())     e.last_name     = t.required;
      if (!form.date_of_birth)        e.date_of_birth = t.required;
      if (!form.phone.trim())         e.phone         = t.required;
      if (!form.patient_address.trim()) e.patient_address = t.required;
      if (!form.primary_diagnosis.trim()) e.primary_diagnosis = t.required;
      if (!form.insurance_provider)   e.insurance_provider = t.required;
    }
    if (s === 3) {
      if (!form.referring_provider_name.trim()) e.referring_provider_name = t.required;
      if (!form.referring_phone.trim())         e.referring_phone = t.required;
      if (form.referring_type === 'self') {
        const petErr = petAnswerError(pets.pets, pets.other);
        if (petErr) e.pets = petErr;
      }
    }
    if (s === 4) {
      if (!form.same_as_provider) {
        if (!form.referrer_first_name.trim()) e.referrer_first_name = t.required;
        if (!form.referrer_last_name.trim())  e.referrer_last_name  = t.required;
        if (!form.referrer_phone.trim())      e.referrer_phone      = t.required;
      }
      if (!form.consent) e.consent = t.consentError;
    }
    return e;
  };

  const next = () => {
    const e = validate(step);
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const back = () => { setStep(s => s - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  // ── Submit ───────────────────────────────────────────────────
  const submit = async () => {
    const e = validate(4);
    if (Object.keys(e).length) { setErrors(e); return; }

    setSubmitting(true);
    setSubmitError('');

    const payload = {
      // Core lead fields
      first_name:   sanitise(form.first_name.trim()),
      last_name:    sanitise(form.last_name.trim()),
      date_of_birth: form.date_of_birth || null,
      phone:        sanitise(form.phone.trim()),
      email:        sanitise(form.referring_email.trim()) || null,
      county:       form.county || null,
      service_type: form.service_type,
      service_lines: form.service_lines.length ? form.service_lines : ['home_health'],
      client_type:  [form.service_type],
      current_status: 'New Lead',
      referral_source: 'Physician / Provider Referral',
      inbound_channel: 'physician_referral',
      // Patient clinical
      patient_address:     sanitise(form.patient_address.trim()) || null,
      primary_diagnosis:   sanitise(form.primary_diagnosis.trim()) || null,
      secondary_diagnosis: sanitise(form.secondary_diagnosis.trim()) || null,
      care_notes:          sanitise(form.care_notes.trim()) || null,
      // Insurance
      insurance_provider: form.insurance_provider || null,
      medicaid_id:        sanitise(form.medicaid_id.trim()) || null,
      insurance_plan:     sanitise(form.insurance_plan.trim()) || null,
      auth_number:        sanitise(form.auth_number.trim()) || null,
      // Referring provider
      referring_type:          form.referring_type,
      referring_physician_id: form.referring_type === 'physician' ? (form.referring_physician_id || null) : null,
      referring_provider_name: sanitise(form.referring_provider_name.trim()) || null,
      referring_practice:      sanitise(form.referring_practice.trim()) || null,
      referring_npi:           sanitise(form.referring_npi.trim()) || null,
      referring_phone:         sanitise(form.referring_phone.trim()) || null,
      referring_fax:           sanitise(form.referring_fax.trim()) || null,
      referring_email:         sanitise(form.referring_email.trim()) || null,
      referring_contact_time:  form.referring_contact_time || null,
      // Submitter
      referrer_first_name:   form.same_as_provider ? null : sanitise(form.referrer_first_name.trim()) || null,
      referrer_last_name:    form.same_as_provider ? null : sanitise(form.referrer_last_name.trim())  || null,
      referrer_phone:        form.same_as_provider ? null : sanitise(form.referrer_phone.trim())      || null,
      referrer_email:        form.same_as_provider ? null : sanitise(form.referrer_email.trim())      || null,
      referrer_relationship: form.same_as_provider ? form.referring_type : (form.referrer_relationship || null),
      // Meta
      notes: form.care_notes ? `Care notes: ${sanitise(form.care_notes.trim())}` : null,
      // Household pets — only a family referral answers them.
      ...(form.referring_type === 'self' ? petColumns(pets.pets, sanitise(pets.other), sanitise(pets.notes)) : {}),
    };

    const { error } = await supabase.from('leads').insert([payload]);

    if (error) {
      console.error('Referral submit error:', error);
      setSubmitError(t.submitErrorMsg);
      setSubmitting(false);
      return;
    }

    // The office hears about it in the team Space from the database trigger
    // (notify_office) on the new lead, not from this page.

    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Styles ──────────────────────────────────────────────────
  const s = {
    wrap:   { minHeight: '100vh', background: 'var(--bg)', padding: '0 0 60px' },
    topbar: { background: 'var(--sidebar-bg)', borderBottom: '1px solid var(--sidebar-border)', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 12 },
    body:   { maxWidth: 720, margin: '0 auto', padding: '32px 16px' },
    heading: { fontSize: 24, fontWeight: 300, color: 'var(--text)', marginBottom: 6, fontFamily: 'Georgia, serif' },
    sub:    { fontSize: 13, color: 'var(--text3)', marginBottom: 28, lineHeight: 1.6 },
    steps:  { display: 'flex', alignItems: 'center', marginBottom: 28, gap: 0 },
    card:   { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, marginBottom: 18, overflow: 'hidden' },
    cardHdr: { padding: '11px 18px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 },
    cardTitle: { fontSize: 11, fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' },
    grid2:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, padding: 18 },
    grid3:  { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, padding: 18 },
    grid1:  { display: 'grid', gridTemplateColumns: '1fr', gap: 14, padding: 18 },
    label:  { fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 4, display: 'block' },
    input:  { padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, color: 'var(--text)', background: 'var(--bg)', width: '100%' },
    inputErr: { padding: '9px 12px', border: '1.5px solid var(--danger)', borderRadius: 8, fontSize: 13, color: 'var(--text)', background: 'var(--bg)', width: '100%' },
    select: { padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, color: 'var(--text3)', background: 'var(--bg)', width: '100%' },
    selectErr: { padding: '9px 12px', border: '1.5px solid var(--danger)', borderRadius: 8, fontSize: 13, color: 'var(--text)', background: 'var(--bg)', width: '100%' },
    textarea: { padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, color: 'var(--text)', background: 'var(--bg)', width: '100%', minHeight: 80, resize: 'vertical', fontFamily: 'inherit' },
    errMsg: { fontSize: 11, color: 'var(--danger)', marginTop: 3 },
    actions: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 },
    btnPrimary: { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 },
    btnBack:    { background: 'transparent', border: '1.5px solid var(--border)', color: 'var(--text2)', borderRadius: 10, padding: '10px 20px', fontSize: 13, cursor: 'pointer' },
  };

  const [publicPhysicians, setPublicPhysicians] = useState([]);
  const [pickedPhysician, setPickedPhysician] = useState(null);
  useEffect(() => { listPublicPhysicians().then(({ data }) => setPublicPhysicians(data || [])); }, []);
  const pickPhysician = (p) => {
    setPickedPhysician(p);
    setForm(f => ({ ...f, referring_physician_id: p ? p.id : null,
      referring_provider_name: p ? `${p.first_name} ${p.last_name}`.trim() + (p.credentials ? `, ${p.credentials}` : '') : f.referring_provider_name,
      referring_practice: p ? (p.facility_name || '') : f.referring_practice }));
  };

  const fld = (field) => ({
    style: errors[field] ? s.inputErr : s.input,
    value: form[field],
    onChange: e => set(field, e.target.value),
  });
  const sel = (field) => ({
    style: errors[field] ? s.selectErr : s.select,
    value: form[field],
    onChange: e => set(field, e.target.value),
  });
  const span2 = { gridColumn: 'span 2' };
  const span3 = { gridColumn: 'span 3' };

  const LangToggle = () => (
    <div style={{ display: 'inline-flex', background: 'var(--border)', borderRadius: 8, padding: 3, marginLeft: 'auto' }}>
      {['en', 'es'].map(l => (
        <button key={l} onClick={() => setLang(l)} style={{
          padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
          fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
          background: lang === l ? 'var(--navy)' : 'transparent',
          color: lang === l ? '#fff' : 'var(--text2)',
        }}>
          {l === 'en' ? '🇺🇸 English' : '🇲🇽 Español'}
        </button>
      ))}
    </div>
  );

  // ── Step progress bar ────────────────────────────────────────
  const StepBar = () => (
    <div style={s.steps}>
      {t.steps.map((label, i) => {
        const num = i + 1;
        const done   = step > num;
        const active = step === num;
        return (
          <div key={num} style={{ display: 'flex', alignItems: 'center', flex: i < t.steps.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
                background: done ? 'var(--accent)' : active ? 'var(--text)' : 'var(--border)',
                color: done || active ? '#fff' : 'var(--text3)',
              }}>
                {done ? '✓' : num}
              </div>
              <span style={{ fontSize: 11, fontWeight: active ? 600 : 400, color: done ? 'var(--accent)' : active ? 'var(--text)' : 'var(--text3)', whiteSpace: 'nowrap' }}>{label}</span>
            </div>
            {i < t.steps.length - 1 && <div style={{ flex: 1, height: 1, background: done ? 'var(--accent)' : 'var(--border)', margin: '0 10px', minWidth: 16 }} />}
          </div>
        );
      })}
    </div>
  );

  // ── Success screen ───────────────────────────────────────────
  if (submitted) return (
    <div style={s.wrap}>
      <div style={s.topbar}>
        <CaeraLogo size={28} showText={false} chrome />
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ch-t1)' }}>{t.topbarTitle}</div>
          <div style={{ fontSize: 11, color: 'var(--ch-t2)' }}>{t.referralSubmitted}</div>
        </div>
        <LangToggle />
      </div>
      <div style={{ ...s.body, textAlign: 'center', paddingTop: 60 }}>
        <div style={{ width: 64, height: 64, background: 'var(--bg2)', borderRadius: '50%', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: 'var(--accent)' }}>✓</div>
        <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>{t.referralSubmitted}</div>
        <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.7, maxWidth: 380, margin: '0 auto 28px' }}>
          {t.successMsgPrefix} <strong style={{ color: 'var(--text)' }}>{form.first_name} {form.last_name}</strong> {t.successMsgSuffix}
        </div>
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 24px', display: 'inline-flex', gap: 14, alignItems: 'center', fontSize: 12, color: 'var(--text3)' }}>
          <span>{t.referenceNumber}</span>
          <strong style={{ color: 'var(--text)', fontSize: 16 }}>{refId}</strong>
        </div>
        <div style={{ marginTop: 32, fontSize: 12, color: 'var(--text3)' }}>
          {t.questionsCall} <a href="tel:4704706640" style={{ color: 'var(--accent)' }}>(470) 470-6640</a> {t.orEmail} <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--accent)' }}>{SUPPORT_EMAIL}</a>
        </div>
      </div>
    </div>
  );

  return (
    <div style={s.wrap}>
      {/* Top bar */}
      <div style={s.topbar}>
        <CaeraLogo size={28} showText={false} chrome />
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ch-t1)' }}>{t.topbarTitle}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t.topbarSub}</div>
        </div>
        <LangToggle />
      </div>

      <div style={s.body}>
        <div style={s.heading}>{t.heading}</div>
        <div style={s.sub}>
          {t.subDesc}{' '}
          {t.fieldsMarkedPrefix} <span style={{ color: 'var(--danger)' }}>*</span> {t.fieldsMarkedSuffix}
        </div>

        <StepBar />

        {/* ── STEP 1: Services, then payer ─────────────────────── */}
        {step === 1 && (
          <>
            <div style={s.card}>
              <div style={s.cardHdr}><span>🧾</span><span style={s.cardTitle}>{t.selectServices} *</span></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, padding: 18 }}>
                {[
                  { key: 'home_health', icon: '🏡', title: t.lineHomeHealth, desc: t.lineHomeHealthDesc },
                  { key: 'dme',         icon: '📦', title: t.lineDme,        desc: t.lineDmeDesc },
                ].map(opt => {
                  const active = form.service_lines.includes(opt.key);
                  return (
                    <div key={opt.key}
                      onClick={() => toggleServiceLine(opt.key)}
                      style={{
                        border: `2px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                        borderRadius: 12, padding: 16, cursor: 'pointer',
                        background: active ? 'var(--bg2)' : 'var(--bg)',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <div style={{
                          width: 18, height: 18, borderRadius: 4, border: `2px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                          background: active ? 'var(--accent)' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          color: '#fff', fontSize: 11, fontWeight: 700, lineHeight: 1,
                        }}>{active ? '✓' : ''}</div>
                        <span style={{ fontSize: 16 }}>{opt.icon}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{opt.title}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, paddingLeft: 28 }}>{opt.desc}</div>
                    </div>
                  );
                })}
              </div>
              {errors.service_lines && <div style={{ padding: '0 18px 14px', fontSize: 12, color: 'var(--danger)' }}>{errors.service_lines}</div>}
            </div>

            <div style={s.card}>
              <div style={s.cardHdr}><span>⚕</span><span style={s.cardTitle}>{t.selectServiceType} *</span></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, padding: 18 }}>
                {[
                  // GAPP authorizes nursing, so it is not offered for a
                  // supplies-only referral.
                  ...(form.service_lines.includes('home_health')
                    ? [{ key: 'gapp', icon: '👶', title: t.gappTitle, desc: t.gappDesc }] : []),
                  { key: 'medicaid',          icon: '🏥', title: t.medicaidTitle,  desc: t.medicaidDesc },
                  { key: 'private_insurance', icon: '🛡️', title: t.insuranceTitle, desc: t.insuranceDesc },
                  { key: 'self_pay',          icon: '💳', title: t.selfPayTitle,   desc: t.selfPayDesc },
                ].map(opt => (
                  <div key={opt.key}
                    onClick={() => set('service_type', opt.key)}
                    style={{
                      border: `2px solid ${form.service_type === opt.key ? 'var(--accent)' : 'var(--border)'}`,
                      borderRadius: 12, padding: 16, cursor: 'pointer',
                      background: form.service_type === opt.key ? 'var(--bg2)' : 'var(--bg)',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <div style={{
                        width: 18, height: 18, borderRadius: '50%', border: `2px solid ${form.service_type === opt.key ? 'var(--accent)' : 'var(--border)'}`,
                        background: form.service_type === opt.key ? 'var(--accent)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        {form.service_type === opt.key && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                      </div>
                      <span style={{ fontSize: 16 }}>{opt.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{opt.title}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, paddingLeft: 28 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
              {errors.service_type && <div style={{ padding: '0 18px 14px', fontSize: 12, color: 'var(--danger)' }}>{errors.service_type}</div>}
            </div>
            <div style={s.actions}>
              <div />
              <button style={s.btnPrimary} onClick={next}>{t.continuePatientInfo}</button>
            </div>
          </>
        )}

        {/* ── STEP 2: Patient + Insurance ──────────────────────── */}
        {step === 2 && (
          <>
            {/* Patient Info */}
            <div style={s.card}>
              <div style={s.cardHdr}><span>👤</span><span style={s.cardTitle}>{t.patientInformation}</span></div>
              <div style={s.grid2}>
                <div>
                  <label style={s.label}>{t.firstName} <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input {...fld('first_name')} placeholder="Maria" />
                  {errors.first_name && <div style={s.errMsg}>{errors.first_name}</div>}
                </div>
                <div>
                  <label style={s.label}>{t.lastName} <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input {...fld('last_name')} placeholder="Garcia" />
                  {errors.last_name && <div style={s.errMsg}>{errors.last_name}</div>}
                </div>
                <div>
                  <label style={s.label}>{t.dob} <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input type="date" {...fld('date_of_birth')} />
                  {errors.date_of_birth && <div style={s.errMsg}>{errors.date_of_birth}</div>}
                </div>
                <div>
                  <label style={s.label}>{t.phoneNumber} <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input {...fld('phone')} placeholder="(470) 000-0000" />
                  {errors.phone && <div style={s.errMsg}>{errors.phone}</div>}
                </div>
                <div style={span2}>
                  <label style={s.label}>{t.homeAddress} <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input {...fld('patient_address')} placeholder="123 Main Street, Cumming, GA 30040" />
                  {errors.patient_address && <div style={s.errMsg}>{errors.patient_address}</div>}
                </div>
                <div>
                  <label style={s.label}>{t.county}</label>
                  <select {...sel('county')}>
                    <option value="">{t.selectCounty}</option>
                    {GA_COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>{t.primaryDiagnosis} <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input {...fld('primary_diagnosis')} placeholder={t.primaryDiagnosisPlaceholder} />
                  {errors.primary_diagnosis && <div style={s.errMsg}>{errors.primary_diagnosis}</div>}
                </div>
                <div style={span2}>
                  <label style={s.label}>{t.secondaryDiagnosis}</label>
                  <input {...fld('secondary_diagnosis')} placeholder={t.secondaryDiagnosisPlaceholder} />
                </div>
                <div style={span2}>
                  <label style={s.label}>{t.careNotes}</label>
                  <textarea style={s.textarea} value={form.care_notes} onChange={e => set('care_notes', e.target.value)}
                    placeholder={t.careNotesPlaceholder} />
                </div>
              </div>
            </div>

            {/* Insurance */}
            <div style={s.card}>
              <div style={s.cardHdr}><span>🏥</span><span style={s.cardTitle}>{t.insuranceFunding}</span></div>
              <div style={s.grid2}>
                <div>
                  <label style={s.label}>{t.insuranceProvider} <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <select {...sel('insurance_provider')}>
                    <option value="">{t.selectPlaceholder}</option>
                    {INSURANCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label[lang]}</option>)}
                  </select>
                  {errors.insurance_provider && <div style={s.errMsg}>{errors.insurance_provider}</div>}
                </div>
                <div>
                  <label style={s.label}>{t.medicaidId}</label>
                  <input {...fld('medicaid_id')} placeholder={t.medicaidIdPlaceholder} />
                </div>
                <div>
                  <label style={s.label}>{t.insurancePlan}</label>
                  <input {...fld('insurance_plan')} placeholder={t.insurancePlanPlaceholder} />
                </div>
                <div>
                  <label style={s.label}>{t.authNumber}</label>
                  <input {...fld('auth_number')} placeholder={t.authNumberPlaceholder} />
                </div>
              </div>
            </div>

            <div style={s.actions}>
              <button style={s.btnBack} onClick={back}>{t.back}</button>
              <button style={s.btnPrimary} onClick={next}>{t.continueReferringProvider}</button>
            </div>
          </>
        )}

        {/* ── STEP 3: Referring Provider ───────────────────────── */}
        {step === 3 && (
          <>
            {/* Referral type */}
            <div style={s.card}>
              <div style={s.cardHdr}><span>🏢</span><span style={s.cardTitle}>{t.referralType}</span></div>
              <div style={{ display: 'flex', gap: 10, padding: '14px 18px' }}>
                {REFERRING_TYPES.map(rt => (
                  <button key={rt.key}
                    onClick={() => set('referring_type', rt.key)}
                    style={{
                      flex: 1, padding: '10px', border: `1.5px solid ${form.referring_type === rt.key ? 'var(--text)' : 'var(--border)'}`,
                      borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', textAlign: 'center',
                      background: form.referring_type === rt.key ? 'var(--text)' : 'var(--bg)',
                      color: form.referring_type === rt.key ? '#fff' : 'var(--text3)',
                    }}
                  >{rt.label[lang]}</button>
                ))}
              </div>
            </div>

            {/* Provider details */}
            <div style={s.card}>
              <div style={s.cardHdr}><span>🩺</span><span style={s.cardTitle}>{t.providerDetails}</span></div>
              {form.referring_type === 'physician' && (
                <div style={{ marginBottom: 14 }}>
                  <label style={s.label}>{t.findPhysician}</label>
                  <PhysicianPicker physicians={publicPhysicians} value={pickedPhysician} onChange={pickPhysician}
                    placeholder={t.findPhysicianPlaceholder} addLabel={t.physicianNotListed} noMatchLabel={t.physicianNoMatch}
                    onAddNew={() => { setPickedPhysician(null); setForm(f => ({ ...f, referring_physician_id: null })); }} />
                </div>
              )}
              <div style={s.grid3}>
                <div style={span2}>
                  <label style={s.label}>{t.physicianAgencyName} <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input {...fld('referring_provider_name')} placeholder={t.physicianAgencyPlaceholder} />
                  {errors.referring_provider_name && <div style={s.errMsg}>{errors.referring_provider_name}</div>}
                </div>
                <div>
                  <label style={s.label}>{t.npiNumber}</label>
                  <input {...fld('referring_npi')} placeholder={t.npiPlaceholder} />
                </div>
                <div style={span3}>
                  <label style={s.label}>{t.practiceOrg}</label>
                  <input {...fld('referring_practice')} placeholder={t.practiceOrgPlaceholder} />
                </div>
                <div>
                  <label style={s.label}>{t.phone} <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input {...fld('referring_phone')} placeholder={t.phonePlaceholder} />
                  {errors.referring_phone && <div style={s.errMsg}>{errors.referring_phone}</div>}
                </div>
                <div>
                  <label style={s.label}>{t.fax}</label>
                  <input {...fld('referring_fax')} placeholder={t.phonePlaceholder} />
                </div>
                <div>
                  <label style={s.label}>{t.email}</label>
                  <input {...fld('referring_email')} placeholder={t.emailPlaceholder} type="email" />
                </div>
                <div style={span3}>
                  <label style={s.label}>{t.preferredContactTime}</label>
                  <select {...sel('referring_contact_time')}>
                    {CONTACT_TIMES.map(ct => <option key={ct.value} value={ct.value}>{ct.label[lang]}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {form.referring_type === 'self' && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>{PET_COPY[lang].section}</div>
                <PetsQuestion lang={lang} value={pets} error={errors.pets}
                  onChange={v => { setPets(v); if (errors.pets) setErrors(e => ({ ...e, pets: petAnswerError(v.pets, v.other) || undefined })); }} />
              </div>
            )}

            <div style={s.actions}>
              <button style={s.btnBack} onClick={back}>{t.back}</button>
              <button style={s.btnPrimary} onClick={next}>{t.continueSubmitterInfo}</button>
            </div>
          </>
        )}

        {/* ── STEP 4: Submitter + Consent ──────────────────────── */}
        {step === 4 && (
          <>
            <div style={s.card}>
              <div style={s.cardHdr}><span>✍️</span><span style={s.cardTitle}>{t.personSubmitting}</span>
                <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>{t.maybeDifferent}</span>
              </div>

              {/* Same-as toggle */}
              <div style={{ margin: '14px 18px 0', padding: '12px 16px', background: 'var(--bg2)', border: '1.5px solid var(--border)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                onClick={() => set('same_as_provider', !form.same_as_provider)}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: 4,
                  background: form.same_as_provider ? 'var(--accent)' : 'transparent',
                  border: `2px solid ${form.same_as_provider ? 'var(--accent)' : 'var(--border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, flexShrink: 0,
                }}>
                  {form.same_as_provider && '✓'}
                </div>
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)' }}>{t.sameAsProviderLabel}</span>
              </div>

              {/* Submitter fields — only shown if not same_as_provider */}
              {!form.same_as_provider && (
                <div style={s.grid2}>
                  <div>
                    <label style={s.label}>{t.firstName} <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <input {...fld('referrer_first_name')} placeholder="Jane" />
                    {errors.referrer_first_name && <div style={s.errMsg}>{errors.referrer_first_name}</div>}
                  </div>
                  <div>
                    <label style={s.label}>{t.lastName} <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <input {...fld('referrer_last_name')} placeholder="Smith" />
                    {errors.referrer_last_name && <div style={s.errMsg}>{errors.referrer_last_name}</div>}
                  </div>
                  <div>
                    <label style={s.label}>{t.phone} <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <input {...fld('referrer_phone')} placeholder={t.phonePlaceholder} />
                    {errors.referrer_phone && <div style={s.errMsg}>{errors.referrer_phone}</div>}
                  </div>
                  <div>
                    <label style={s.label}>{t.email}</label>
                    <input {...fld('referrer_email')} placeholder="jane@email.com" type="email" />
                  </div>
                  <div style={span2}>
                    <label style={s.label}>{t.relationshipToPatient}</label>
                    <select {...sel('referrer_relationship')}>
                      {RELATIONSHIPS.map(r => <option key={r.value} value={r.value}>{r.label[lang]}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* Consent */}
              <div style={{ padding: '14px 18px', borderTop: '1px solid var(--border)', display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: form.same_as_provider ? 14 : 0 }}>
                <div
                  onClick={() => set('consent', !form.consent)}
                  style={{
                    width: 20, height: 20, borderRadius: 4, flexShrink: 0, marginTop: 1, cursor: 'pointer',
                    background: form.consent ? 'var(--accent)' : 'transparent',
                    border: `2px solid ${form.consent ? 'var(--accent)' : errors.consent ? 'var(--danger)' : 'var(--border)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12,
                  }}
                >{form.consent && '✓'}</div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, cursor: 'pointer' }}
                    onClick={() => set('consent', !form.consent)}>
                    {t.consentText}
                  </label>
                  {errors.consent && <div style={s.errMsg}>{errors.consent}</div>}
                </div>
              </div>
            </div>

            {submitError && (
              <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: 'var(--danger)', marginBottom: 14 }}>
                {submitError}
              </div>
            )}

            <div style={s.actions}>
              <button style={s.btnBack} onClick={back}>{t.back}</button>
              <button style={{ ...s.btnPrimary, opacity: submitting ? 0.7 : 1 }} onClick={submit} disabled={submitting}>
                {submitting ? t.submitting : t.submitReferral}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
